import { useState } from "react";
import { toast } from "sonner";
import { Flame, Loader2, Sparkles } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { logger } from "../lib/logger";
import { CopyBtn } from "../components/CopyBtn";
import { ShareCardActions } from "../components/ShareCardActions";
import { useAuth } from "../contexts/AuthContext";

const INTENSITY = [
  { id: "gentle", label: "Gentle", sub: "Friendly feedback" },
  { id: "direct", label: "Direct", sub: "Honest, no sugar" },
  { id: "savage", label: "Savage", sub: "Brutal but useful" },
];

function gradeColor(g) {
  return { A: "bg-green-500", B: "bg-emerald-500", C: "bg-amber-500", D: "bg-orange-500", F: "bg-red-500" }[g] || "bg-zinc-500";
}

export default function RoastPage() {
  const { user, refreshUser } = useAuth();
  const [draft, setDraft] = useState("");
  const [intensity, setIntensity] = useState("savage");
  const [platform, setPlatform] = useState("twitter");
  const [applyClone, setApplyClone] = useState(true);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const roast = async () => {
    setErr("");
    if (draft.trim().length < 10) {
      setErr("Paste at least a sentence to roast.");
      return;
    }
    setLoading(true);
    setRes(null);
    try {
      const { data } = await api.post("/roast", { draft, intensity, platform, apply_clone: applyClone });
      setRes(data.roast);
      toast.success("Roast complete. Keep what stings.");
      await refreshUser();
    } catch (e) {
      logger.error("Roast error", e);
      setErr(formatError(e.response?.data?.detail) || "Roast failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="roast-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-500 px-3 py-1 text-xs font-bold tracking-wider uppercase mb-3">
            <Flame className="w-3.5 h-3.5" /> Brutally honest
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">
            Roast my copy.
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Paste your draft. Get torn apart with specific problems, hook/CTA grades, and a punched-up rewrite in your voice.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div>
              <label className="text-xs font-bold tracking-wider uppercase text-zinc-400 block mb-2">Your draft</label>
              <textarea
                data-testid="roast-draft"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={10}
                maxLength={4000}
                placeholder="Paste the post / caption / hook you're about to send…"
                className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none focus:border-brand resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-wider uppercase text-zinc-400 block mb-2">Intensity</label>
              <div className="grid grid-cols-3 gap-2">
                {INTENSITY.map((i) => {
                  const on = intensity === i.id;
                  return (
                    <button
                      key={i.id}
                      onClick={() => setIntensity(i.id)}
                      data-testid={`intensity-${i.id}`}
                      className={`p-2 text-left border ${on ? "bg-red-500 text-white border-red-500" : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300"}`}
                    >
                      <p className="font-bold text-sm">{i.label}</p>
                      <p className={`text-[10px] ${on ? "text-white/80" : "text-zinc-500"}`}>{i.sub}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={applyClone} onChange={(e) => setApplyClone(e.target.checked)} className="accent-brand" />
              <span className="text-zinc-700 dark:text-zinc-300">Rewrite in my Clone's voice</span>
            </label>
            {err && <p className="text-sm text-red-500" data-testid="roast-error">{err}</p>}
            <button
              onClick={roast}
              disabled={loading}
              data-testid="roast-run-btn"
              className="w-full bg-red-500 text-white font-semibold py-3.5 hover:bg-red-600 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Roasting…</>) : (<><Flame className="w-5 h-5" /> Roast it (1 cr)</>)}
            </button>
          </div>

          <div className="md:col-span-3">
            {!res && !loading && (
              <div className="h-full min-h-[320px] border-2 border-dashed border-zinc-200 dark:border-zinc-800 flex items-center justify-center p-8 text-center">
                <div>
                  <Flame className="w-10 h-10 text-zinc-300 dark:text-zinc-700 mx-auto mb-3" />
                  <p className="text-sm text-zinc-400">The roast appears here.</p>
                </div>
              </div>
            )}
            {loading && (
              <div className="h-full min-h-[320px] flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-red-500" />
              </div>
            )}
            {res && (
              <div className="space-y-4" data-testid="roast-result">
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-4xl font-black tracking-tighter text-zinc-950 dark:text-white">{res.score}/10</div>
                    <div className="flex items-center gap-2">
                      {res.hook_grade && (
                        <span className={`w-8 h-8 ${gradeColor(res.hook_grade)} text-white font-black text-sm flex items-center justify-center`}>
                          {res.hook_grade}
                        </span>
                      )}
                      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Hook</span>
                      {res.cta_grade && (
                        <>
                          <span className={`w-8 h-8 ${gradeColor(res.cta_grade)} text-white font-black text-sm flex items-center justify-center ml-2`}>
                            {res.cta_grade}
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">CTA</span>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-base font-bold text-zinc-900 dark:text-white italic">"{res.verdict}"</p>
                </div>

                {res.problems && res.problems.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 p-4">
                    <p className="text-xs font-bold tracking-wider uppercase text-red-600 dark:text-red-400 mb-2">Problems</p>
                    <ul className="space-y-2 text-sm text-red-900 dark:text-red-200">
                      {res.problems.map((p, i) => <li key={i} className="leading-relaxed">• {p}</li>)}
                    </ul>
                  </div>
                )}

                {res.rewrite && (
                  <div className="bg-gradient-to-br from-brand/5 to-green-500/5 border border-brand/20 p-4" data-testid="roast-rewrite">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold tracking-wider uppercase text-brand flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> The fixed version
                      </p>
                      <CopyBtn text={res.rewrite} label="Copy" />
                    </div>
                    <p className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap leading-relaxed">
                      {res.rewrite}
                    </p>
                  </div>
                )}

                <div className="pt-2">
                  <p className="text-xs font-bold tracking-wider uppercase text-zinc-400 mb-2">Share the roast</p>
                  <ShareCardActions
                    kind="roast"
                    data={{
                      verdict: res.verdict,
                      score: res.score,
                      rewrite: res.rewrite,
                    }}
                    refCode={user?.referral_code || ""}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
